import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useTrips } from '../data/tripsApi';
import { useGalleries, usePage } from '../data/cmsApi';
import { readBlock } from './cms/pageSchemas';
import { unsplash, unsplashSrcSet } from '../lib/imgUrl';

const API = process.env.REACT_APP_BACKEND_URL;

// Marketing section: titles+descriptions+images all CMS-driven (page='home',
// blocks 'feat1' & 'feat2'). Empty fields fall back to i18n strings + the
// first gallery / next ascent for the cover image.
const FALLBACK_IMG_1 = 'https://images.unsplash.com/photo-1551632811-561732d1e306';
const FALLBACK_IMG_2 = 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b';

const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API}${url}`;
};

const FeaturedPrograms = () => {
  const { t, i18n } = useTranslation();
  const { trips } = useTrips('expedition', i18n.language);
  const { galleries } = useGalleries(i18n.language);
  const { page } = usePage('home');
  const blocks = page?.blocks || {};
  const lang = i18n.language;

  const firstGallery = (galleries || [])[0];
  const nextAscent = (trips || [])[0];

  const card = (blockKey, fallbackImg, fallbackTitle, fallbackDesc, fallbackLink, testid) => {
    const b = readBlock(blocks, 'home', blockKey, lang);
    return {
      title: b.title || fallbackTitle,
      description: b.description || fallbackDesc,
      image: resolveImg(b.image) || fallbackImg,
      to: b.link || fallbackLink,
      testid,
    };
  };

  const programs = [
    card(
      'feat1',
      (firstGallery && firstGallery.cover_image) || FALLBACK_IMG_1,
      t('featured.program1.title'),
      t('featured.program1.description'),
      firstGallery ? '/about' : '/hymettus',
      'featured-card-1',
    ),
    card(
      'feat2',
      (nextAscent && nextAscent.image) || FALLBACK_IMG_2,
      t('featured.program2.title'),
      t('featured.program2.description'),
      nextAscent ? `/expeditions/${nextAscent.id}` : '/expeditions',
      'featured-card-2',
    ),
  ];

  return (
    <section className="py-20 bg-white" data-testid="home-featured">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="sr-only">{t('featured.section.title', 'Featured Programs')}</h2>
        <div className="grid md:grid-cols-2 gap-12">
          {programs.map((program) => (
            <Card key={program.testid} data-testid={program.testid} className="overflow-hidden border border-gray-100 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
              <div className="relative h-64 overflow-hidden">
                <img loading="lazy" decoding="async"
                  src={unsplash(program.image, 800)}
                  srcSet={unsplashSrcSet(program.image, [400, 800, 1200]) || undefined}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  width={800}
                  height={600}
                  alt={program.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{program.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{program.description}</p>
                <Button asChild variant="outline" className="group">
                  <Link to={program.to}>
                    {t('featured.exploreMore')}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedPrograms;
